/* A browser frame that lays a piece of the real system out for its own width and scales it to fit.
   full ≥ 1100 (sidebar) · compact ≥ 700 (no sidebar) · narrow below (one column, laid out for a phone – at least 390 px,
   so on a phone it renders 1:1 and stays legible). The script inside only runs while in view. */
import { useEffect, useRef, useState } from "react";

export const geoFor = (w, { fullWidth = 1360, compactMin = 1040, narrowMin = 390 } = {}) => {
  const mode = w < 700 ? "narrow" : w < 1100 ? "compact" : "full";
  const vw = mode === "narrow" ? Math.max(narrowMin, w) : mode === "compact" ? Math.max(compactMin, w) : fullWidth;
  return { mode, vw, s: w / vw };
};

/* keeps real navigation out of a preview: no pointer events, no tab stops, no links */
export const Inert = ({ children, className = "" }) => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.setAttribute("inert", "");
  }, []);
  return (
    <div ref={ref} className={`pointer-events-none select-none ${className}`} aria-hidden="true">
      {children}
    </div>
  );
};

export const Stage = ({ url, caption, height = 820, narrowHeight = 760, widths, children, testId, className = "", attrs, onActive }) => {
  const ref = useRef(null);
  const [geo, setGeo] = useState({ mode: "full", vw: widths?.fullWidth || 1360, s: 0.86 });
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setGeo(geoFor(e.contentRect.width, widths)));
    ro.observe(el);
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[entries.length - 1];
        setActive(e.isIntersecting);
        onActive?.(e.isIntersecting);
      },
      { threshold: 0.05 }
    );
    io.observe(el);
    return () => {
      ro.disconnect();
      io.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const ih = geo.mode === "narrow" ? narrowHeight : height;
  return (
    <div className={`mb-frame ${className}`} data-testid={testId} data-mode={geo.mode} data-active={active ? "1" : "0"} {...attrs}>
      <div className="mb-chrome" aria-hidden="true">
        <span className="mb-dots"><i /><i /><i /></span>
        <span className="mb-url" key={url} data-testid={testId ? `${testId}-url` : undefined}>{url}</span>
        <span className="mb-cap">{caption}</span>
      </div>
      <div ref={ref} className="mb-view" style={{ height: ih * geo.s }}>
        <div className="mb-scale" style={{ transform: `scale(${geo.s})`, width: geo.vw, height: ih }}>
          {children({ mode: geo.mode, active, width: geo.vw, height: ih })}
        </div>
      </div>
    </div>
  );
};
